// Iter-254 / 255 · Transportation driver-sign A4 print template.
//
// Renders a single-page A4 landscape sheet the driver hands out at
// the hotel reception: Cocoon butterfly logo header, huge guest name,
// "Pick-up: <hotel>" subline, and a footer detail block with
// Day/Date/Time, Address, Phone and a Notes line (typed by the team in
// the appointment's Notes field, or left blank to hand-write).
//
// Iter-255 hard rules:
//   • Strictly ONE page — `size: A4 landscape` + `body { height:
//     190mm; overflow: hidden; }` prevents any content from spilling
//     into a second sheet even when the address wraps long.
//   • The flower/butterfly logo is served from the app's public
//     folder as `/cocoon-butterfly.png` so it prints even when the
//     browser blocks external CDNs.
//
// Contract:
//   printTransportationSlip({ guestName, hotel, address, phone,
//                             startIso, notes })

const escapeHtml = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => (
  { "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]
));

const formatDay = (iso) => {
  if (!iso) return "\u2014";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "Asia/Makassar",
    });
  } catch { return "\u2014"; }
};

// Public path for the Cocoon butterfly logo. Served straight from
// `/app/frontend/public/cocoon-butterfly.png` so print jobs never wait
// on an external CDN handshake.
const LOGO_SRC = "/cocoon-butterfly.png";

export function buildTransportationSlipHtml({
  guestName, hotel, address, phone, startIso, notes,
}) {
  const dayLine = formatDay(startIso);
  const noteText = String(notes ?? "").trim();
  return `<!doctype html>
<html><head>
  <meta charset="utf-8" />
  <title>Cocoon Medical Spa \u2014 Transportation</title>
  <style>
    /* Iter-377 · One-page guarantee for the driver pickup slip.
       The slip MUST fit on exactly ONE sheet regardless of:
         - printer orientation (portrait vs landscape — some drivers
           ignore @page size and force portrait),
         - "Print headers and footers" being ON (steals vertical
           space from the printable area),
         - long hotel / address / phone text wrapping onto more lines.
       We now ship the sheet at a FIXED landscape canvas (A4 landscape:
       297mm × 210mm) and let JS measure the overflow and apply a
       uniform CSS transform scale BEFORE window.print() fires so
       the whole slip always shrinks into the printable area. CSS
       overflow:hidden is still kept as a last-resort safety net. */
    @page { size: A4 landscape; margin: 8mm; }
    html, body { margin: 0; padding: 0; }
    body {
      font-family: 'Helvetica Neue', Arial, sans-serif;
      color: #111;
      box-sizing: border-box;
      overflow: hidden;
    }
    * { box-sizing: border-box; }
    /* The sheet is sized to the A4 landscape printable area minus
       @page margins (297 - 16 = 281mm wide, 210 - 16 = 194mm tall).
       The JS below measures its scrollHeight and scales it DOWN if
       long text pushed the real height past 194mm. Scaling never goes
       UP — short slips stay at full size. */
    .sheet-wrap {
      width: 281mm;
      height: 194mm;
      overflow: hidden;
      transform-origin: top left;
    }
    .sheet {
      width: 100%;
      min-height: 100%;
      display: flex;
      flex-direction: column;
      padding: 0;
    }
    .brand { text-align: center; }
    .brand-logo { height: 24mm; width: auto; display: inline-block; }
    .brand-mark {
      font-family: 'Georgia', 'Times New Roman', serif;
      letter-spacing: 0.32em;
      font-weight: 400;
      font-size: 30pt;
      line-height: 1;
      margin-top: 2mm;
    }
    .brand-sub {
      font-size: 9pt;
      letter-spacing: 0.5em;
      margin-top: 3mm;
      color: #333;
      border-top: 1px solid #111;
      border-bottom: 1px solid #111;
      display: inline-block;
      padding: 1mm 3mm;
    }
    .guest {
      margin-top: 10mm;
      text-align: center;
      font-weight: 800;
      /* Iter-… · guest name +20% (42pt → 50.4pt) — the driver reads this
         from across a hotel lobby. */
      font-size: 50.4pt;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      line-height: 1;
      word-break: break-word;
    }
    .pickup {
      margin-top: 6mm;
      text-align: center;
      font-weight: 700;
      font-size: 24pt;
      line-height: 1.15;
      word-break: break-word;
    }
    .footer {
      margin-top: auto;
      /* Iter-… · the whole Day/Date/Time · Address · Phone · Notes block sits
         5cm HIGHER up the sheet (driver asked for it away from the bottom
         edge where the paper is gripped / folded). */
      margin-bottom: 50mm;
      padding-top: 6mm;
      /* Detail block +20% (12pt → 14.4pt) and nudged 1cm right so the
         driver's thumb never covers the first letters. */
      padding-left: 10mm;
      font-size: 14.4pt;
      line-height: 1.4;
    }
    .footer .row { display: flex; margin-bottom: 1mm; }
    .footer .row .k { width: 50mm; font-weight: 700; }
    .footer .row .sep { width: 6mm; text-align: center; font-weight: 700; }
    .footer .row .v { flex: 1; font-weight: 600; word-break: break-word; }
    /* Hand-written notes line — printed when the team left Notes empty. */
    .footer .row .v.blank {
      border-bottom: 1px solid #111;
      min-height: 7mm;
    }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      /* Belt-and-braces: block ANY browser-inserted page break so the
         JS auto-fit scale is never overridden. */
      html, body, .sheet-wrap, .sheet, .sheet * {
        page-break-inside: avoid !important;
        page-break-before: avoid !important;
        page-break-after: avoid !important;
        break-inside: avoid !important;
        break-before: avoid !important;
        break-after: avoid !important;
      }
    }
  </style>
</head><body>
  <div class="sheet-wrap" id="sheet-wrap">
  <div class="sheet" id="sheet">
    <div class="brand">
      <img class="brand-logo" src="${LOGO_SRC}" alt="Cocoon" onerror="this.style.display='none'" />
      <div class="brand-mark">COCOON</div>
      <div class="brand-sub">MEDICAL SPA</div>
    </div>

    <div class="guest" data-testid="driver-sign-guest">${escapeHtml((guestName || "Guest").toUpperCase())}</div>
    <div class="pickup" data-testid="driver-sign-pickup">Pick-up: ${escapeHtml(hotel || "\u2014")}</div>

    <div class="footer">
      <div class="row"><div class="k">Day/Date/Time</div><div class="sep">:</div><div class="v" data-testid="driver-sign-when">${escapeHtml(dayLine)}</div></div>
      <div class="row"><div class="k">Address</div><div class="sep">:</div><div class="v" data-testid="driver-sign-address">${escapeHtml(address || "\u2014")}</div></div>
      <div class="row"><div class="k">Phone</div><div class="sep">:</div><div class="v" data-testid="driver-sign-phone">${escapeHtml(phone || "\u2014")}</div></div>
      <div class="row"><div class="k">Notes</div><div class="sep">:</div><div class="v${noteText ? "" : " blank"}" data-testid="driver-sign-notes">${escapeHtml(noteText)}</div></div>
    </div>
  </div>
  </div>
  <script>
    // Iter-377 · auto-fit the slip into a single page.
    //  (1) Wait for the logo image to load (or error out) so the
    //      measurement reflects the final rendered height.
    //  (2) Compare the sheet's content scrollHeight against the
    //      wrapper's fixed printable height (194mm → ~733px @ 96dpi).
    //      If content overflows (long hotel / address text), apply a
    //      uniform CSS transform scale so the WHOLE sheet shrinks
    //      proportionally until it fits — guaranteeing one page.
    //  (3) Mirror the wrapper's height after scaling so no browser
    //      heuristic decides there is a "widow" line that deserves
    //      a fresh page.
    //  (4) Only AFTER the fit is applied do we call window.print().
    function fitAndPrint() {
      try {
        var wrap = document.getElementById('sheet-wrap');
        var sheet = document.getElementById('sheet');
        if (wrap && sheet) {
          var wrapH = wrap.getBoundingClientRect().height;
          // scrollHeight is content's real height (unclipped by overflow).
          var contentH = sheet.scrollHeight;
          var scale = 1;
          if (contentH > wrapH && contentH > 0) {
            scale = Math.max(0.3, wrapH / contentH);
            sheet.style.transformOrigin = 'top left';
            sheet.style.transform = 'scale(' + scale + ')';
            sheet.style.width = (100 / scale) + '%';
          }
        }
      } catch (e) { /* defensive — print anyway on any measurement error */ }
      setTimeout(function(){ window.print(); }, 120);
    }
    window.addEventListener('load', function(){
      var img = document.querySelector('.brand-logo');
      if (img && !img.complete) {
        img.addEventListener('load',  fitAndPrint, { once: true });
        img.addEventListener('error', fitAndPrint, { once: true });
      } else {
        fitAndPrint();
      }
    });
  <\/script>
</body></html>`;
}

export function printTransportationSlip(details) {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.open();
  w.document.write(buildTransportationSlipHtml(details));
  w.document.close();
}
